import {
  getSessionById,
  notifyDoctors,
  startInstantSession
} from '@cannect/services/resources/instantConsultation/requests'
import { useBrand } from 'hooks/useBrand'
import { useInstantConsultation } from 'hooks/useInstantConsultation'
import { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

export type InstantConsultationLocationState = {
  resumeSessionId?: number
}

/** Query param que permite compartilhar um link de retomada (ex.: ?session=1234). */
export const RESUME_QUERY_PARAM = 'session'

/** Lê o id de retomada da query string, ignorando valores não numéricos. */
export function getResumeSessionIdFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get(RESUME_QUERY_PARAM)
  if (!raw) return null
  const id = Number(raw)
  return Number.isInteger(id) && id > 0 ? id : null
}

type InstantConsultationResumeHandlerProps = {
  /** Id vindo do link (?session=), capturado pelo componente raiz. */
  linkResumeSessionId?: number | null
}

export function InstantConsultationResumeHandler({
  linkResumeSessionId = null
}: InstantConsultationResumeHandlerProps) {
  const location = useLocation<InstantConsultationLocationState>()
  const history = useHistory()
  const { config } = useBrand()
  const { hydrateResumeSession } = useInstantConsultation()
  const processedIdRef = useRef<number | null>(null)

  const basePath = `${config.pathPrefix ?? ''}/consulta-na-hora`

  useEffect(() => {
    // O link compartilhável (?session=) e a navegação interna (location.state)
    // alimentam a mesma retomada. O id do link chega por prop porque a query
    // já pode ter sido limpa quando este componente monta.
    const id = location.state?.resumeSessionId ?? linkResumeSessionId
    if (!id) {
      processedIdRef.current = null
      return
    }
    if (processedIdRef.current === id) {
      return
    }
    // Só o link compartilhável precisa reingressar na fila: a navegação interna
    // (SessionCallCard) já chama start antes de empurrar o resumeSessionId.
    const cameFromLink = !location.state?.resumeSessionId

    processedIdRef.current = id
    ;(async () => {
      try {
        const session = await getSessionById(id)

        // Quem fechou a aba pode ter saído da fila: reentra antes de hidratar.
        // O endpoint é idempotente (responde already_in_queue) e só vale para
        // sessão paga e ainda sem médico atribuído.
        const shouldRejoinQueue =
          cameFromLink &&
          Boolean(session?.uuid) &&
          !session?.concluded_at &&
          !session?.precriber_id &&
          session?.status_payment === 'paid'

        if (shouldRejoinQueue) {
          try {
            await startInstantSession(session.uuid as string)
            await notifyDoctors(session.uuid as string)
          } catch {
            // noop - best-effort; o polling da fila segue valendo
          }
        }

        hydrateResumeSession(session)
      } catch {
        toast.error('Não foi possível retomar a consulta.')
        processedIdRef.current = null
      } finally {
        // Limpa apenas o state da navegação interna. A query do link NÃO é
        // limpa aqui: o replace remonta a árvore (Provider incluso) e o step
        // recém-hidratado se perderia, jogando a pessoa de volta na triagem.
        if (location.state?.resumeSessionId) {
          history.replace({ pathname: basePath, state: {} })
        }
      }
    })()
  }, [location.state, linkResumeSessionId, hydrateResumeSession, history, basePath])

  return null
}
